import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { companyInfo, serviceTypes, locations } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    location: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est obligatoire';
    }

    // Au moins téléphone OU email doit être fourni
    if (!formData.phone.trim() && !formData.email.trim()) {
      newErrors.contact = 'Veuillez fournir au moins un téléphone ou un email';
    }

    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Veuillez sélectionner un type de service';
    }

    if (!formData.location) {
      newErrors.location = 'Veuillez sélectionner une localisation';
    }

    if (!formData.description.trim() || formData.description.trim().length < 10) {
      newErrors.description = 'Veuillez décrire votre projet (minimum 10 caractères)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setIsSubmitting(true);

    try {
      // Préparer le payload - envoyer des chaînes vides pour les champs optionnels
      // Le backend gère la conversion des chaînes vides en None
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        serviceType: formData.serviceType,
        location: formData.location,
        description: formData.description.trim()
      };

      // Utiliser la variable d'environnement pour l'URL de l'API
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${apiUrl}/api/soumission`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Merci, votre demande a bien été envoyée.', {
        description: 'Nous vous contacterons dans les plus brefs délais.',
        duration: 5000
      });

      // Reset form UNIQUEMENT en cas de succès
      setFormData({
        name: '',
        phone: '',
        email: '',
        serviceType: '',
        location: '',
        description: ''
      });
      setErrors({});
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      let errorMessage = 'Une erreur est survenue lors de l\'envoi.';
      
      if (error.response) {
        // Le serveur a répondu avec un code d'erreur
        if (error.response.status === 422) {
          errorMessage = 'Erreur de validation: ' + (error.response.data.detail || 'Veuillez vérifier vos informations.');
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // La requête a été faite mais pas de réponse
        errorMessage = 'Impossible de contacter le serveur. Veuillez réessayer.';
      }
      
      toast.error('Erreur d\'envoi', {
        description: errorMessage + ' Vous pouvez nous appeler au (514) 714-4245.',
        duration: 7000
      });
      
      // NE PAS réinitialiser le formulaire en cas d'erreur
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-contact">
      <SEO 
        title="Contact et Soumission Gratuite | Élagage Québec | PaysFor"
        description="Contactez PaysFor pour une soumission gratuite. Services d'abattage d'arbres et élagage à Québec. Réponse rapide pour résidentiel et commercial dans la Capitale-Nationale."
        keywords="soumission élagage Québec, contact arboriste Québec, devis abattage arbres Capitale-Nationale, estimation coupe arbre Québec"
        canonical="/contact"
      />
      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="display-medium">Contactez-nous</h1>
          <p className="body-large">
            Obtenez une soumission gratuite pour vos projets arboricoles, paysagers et forestiers
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content">
        <div className="container">
          <div className="contact-layout">
            {/* Contact Info */}
            <div className="contact-info-section">
              <h2 className="heading-2">Coordonnées</h2>
              
              <div className="contact-info-items">
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <Phone size={28} />
                  </div>
                  <div>
                    <h3 className="heading-3">Téléphone</h3>
                    <a href={`tel:${companyInfo.phone}`} className="contact-link">
                      {companyInfo.phone}
                    </a>
                    <p className="caption">Appelez-nous directement</p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <Mail size={28} />
                  </div>
                  <div>
                    <h3 className="heading-3">Courriel</h3>
                    <a href={`mailto:${companyInfo.email}`} className="contact-link">
                      {companyInfo.email}
                    </a>
                    <p className="caption">Écrivez-nous</p>
                  </div>
                </div>

                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <MapPin size={28} />
                  </div>
                  <div>
                    <h3 className="heading-3">Localisation</h3>
                    <p className="body-medium">{companyInfo.location}</p>
                  </div>
                </div>
              </div>

              <div className="service-areas-box">
                <h3 className="heading-3">Zone desservie</h3>
                <p className="body-medium" style={{ marginBottom: 'var(--spacing-small)' }}>
                  <strong>{companyInfo.serviceArea}</strong>
                </p>
                <p className="caption" style={{ fontStyle: 'italic' }}>
                  {companyInfo.serviceAreaNote}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="form-card">
                <h2 className="heading-2">Demande de soumission gratuite</h2>
                <p className="body-medium form-intro">
                  Remplissez ce formulaire et nous vous contacterons rapidement pour discuter de votre projet.
                </p>

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Nom complet <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      placeholder="Votre nom"
                    />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone" className="form-label">
                        Téléphone {!formData.email && <span className="required">*</span>}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`form-input ${errors.contact ? 'error' : ''}`}
                        placeholder="(514) 000-0000"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="email" className="form-label">
                        Courriel {!formData.phone && <span className="required">*</span>}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`form-input ${errors.email || errors.contact ? 'error' : ''}`}
                        placeholder="votre@email.com"
                      />
                      {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>
                  </div>
                  {errors.contact && (
                    <span className="error-message error-contact">{errors.contact}</span>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="serviceType" className="form-label">
                        Type de service <span className="required">*</span>
                      </label>
                      <select
                        id="serviceType"
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleChange}
                        className={`form-select ${errors.serviceType ? 'error' : ''}`}
                      >
                        <option value="">Sélectionnez un service</option>
                        {serviceTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      {errors.serviceType && <span className="error-message">{errors.serviceType}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="location" className="form-label">
                        Localisation <span className="required">*</span>
                      </label>
                      <select
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        className={`form-select ${errors.location ? 'error' : ''}`}
                      >
                        <option value="">Sélectionnez une zone</option>
                        {locations.map((loc) => (
                          <option key={loc.value} value={loc.value}>
                            {loc.label}
                          </option>
                        ))}
                      </select>
                      {errors.location && <span className="error-message">{errors.location}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="description" className="form-label">
                      Description du projet <span className="required">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      className={`form-textarea ${errors.description ? 'error' : ''}`}
                      rows="5"
                      placeholder="Décrivez votre projet en détail..."
                    ></textarea>
                    {errors.description && <span className="error-message">{errors.description}</span>}
                  </div>

                  <button 
                    type="submit" 
                    className="btn-cta form-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                    {!isSubmitting && <Send size={20} style={{ marginLeft: '8px' }} />}
                  </button>

                  <p className="caption form-note">
                    <span className="required">*</span> Champs obligatoires • Au moins un téléphone ou email requis
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
